import React from 'react';
import moment from 'moment';
import styles from './SideDrawer.module.css';

const formatInt = (value) => {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(value));
};

const formatSignedInt = (value) => {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }
  const sign = value >= 0 ? '+' : '−';
  return `${sign}${formatInt(Math.abs(value))}`;
};

const formatPct = (value) => {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }
  return `${Number(value).toFixed(1)}%`;
};

const formatPrice = (value) => {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }
  const decimals = Math.abs(value) >= 10 ? 2 : 4;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

const formatDate = (value) => {
  const parsed = moment(value, ['YYYY-MM-DD', 'MM/DD/YY', moment.ISO_8601], true);
  if (!parsed.isValid()) {
    return value;
  }
  return parsed.format('MMM D, YYYY');
};

const DEFAULT_META = {
  dealer: {
    label: 'Dealer',
    colorVar: '--series-dealer',
  },
  assetManager: {
    label: 'Asset Managers',
    colorVar: '--series-asset',
  },
  leveragedMoney: {
    label: 'Leveraged Funds',
    colorVar: '--series-lev',
  },
};

const buildColorStyle = (meta) => {
  if (meta?.color) {
    return { color: meta.color };
  }
  if (meta?.colorVar) {
    return { color: `var(${meta.colorVar})` };
  }
  return { color: 'var(--color-accent)' };
};

const ACTOR_ORDER = ['dealer', 'assetManager', 'leveragedMoney'];

const CftcTooltipCard = ({ payload, actorMeta }) => {
  const meta = {
    dealer: { ...DEFAULT_META.dealer, ...actorMeta?.dealer },
    assetManager: { ...DEFAULT_META.assetManager, ...actorMeta?.assetManager },
    leveragedMoney: { ...DEFAULT_META.leveragedMoney, ...actorMeta?.leveragedMoney },
  };

  return (
    <div className={styles.cftcTooltipCard}>
      <div className={styles.cftcTooltipHeader}>
        <div className={styles.cftcTooltipTitle}>
          {formatDate(payload.dateISO)} • Weekly CFTC
        </div>
        {payload.regimeLabel && (
          <span
            aria-label={`Regime ${payload.regimeLabel}`}
            className={styles.cftcTooltipBadge}
          >
            {payload.regimeLabel}
          </span>
        )}
      </div>
      <div className={styles.cftcTooltipMeta}>
        <div>Open Interest: {formatInt(payload.openInterest)} contracts</div>
        {payload.price != null && <div>Price: {formatPrice(payload.price)}</div>}
      </div>

      {payload.deskRead && (
        <div className={styles.cftcTooltipDesk}>
          <div className={styles.cftcTooltipDeskLabel}>Desk Read</div>
          <div className={styles.cftcTooltipDeskBody}>{payload.deskRead}</div>
        </div>
      )}

      <div className={styles.cftcTooltipActors}>
        {ACTOR_ORDER.map((key) => {
          const actor = payload.actors[key];
          const actorMetaItem = meta[key];
          const labelStyle = buildColorStyle(actorMetaItem);
          return (
            <div key={key} className={styles.cftcTooltipActor}>
              <div className={styles.cftcTooltipActorRow}>
                <div>
                  <div className={styles.cftcTooltipActorLabel} style={labelStyle}>
                    {actorMetaItem.label}
                  </div>
                  <div className={styles.cftcTooltipActorMeta}>
                    Net % OI: {formatPct(actor.netPctOI)}
                  </div>
                </div>
                <div className={styles.cftcTooltipActorRight}>
                  <div className={styles.cftcTooltipActorNet} style={labelStyle}>
                    Net: {formatSignedInt(actor.net)}
                  </div>
                  <div className={styles.cftcTooltipActorMeta}>
                    L / S: {formatInt(actor.long)} / {formatInt(actor.short)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CftcTooltipCard;
